import { AbsgArrayHelper } from '@absg/angular-common';
import { DateTimeHelper } from '@absg/angular-platform';
import { ProductGroupProduct } from '@absg/api-formulary';
import { DispenseApi, DispenseDetailSnapshot, DispenseSnapshot, DispenseSnapshotPrintDataSearchParameter } from '@absg/api-inventory';
import { MedOrderPrintDetail } from '@absg/api-inventory/model/medOrderPrintDetail';
import { PatientLite, PhysicalAddress, PracticeClinicDetail, Telephone } from '@absg/api-party';
import {
  AdditionalPointInTimeData, BaseService, CalculationHelper, DispenseItem, DispenseSnapshotViewModel, InventoryItem, LotDetail, NdcDetail, Patient, Physician, RequiredNote,
  StoragePosition,
} from '@absg/nim-client-shared';
import { Injectable } from '@angular/core';
import { DispenseSelectedGroup } from 'dispense.models/dispense-selected-group.model';
import { DispenseDetailSnapshotViewModel } from 'dispense.models/dispenseDetailSnapshot.model';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { InventoryActivityTypes } from 'shared.root/enums/inventory-activity-types.enum';

import { DispenseCommonOperationsService } from './dispense-common-operations.service';

@Injectable({ providedIn: 'root' })
export class DispensePointInTimeDataService extends BaseService {
  pointInTimeObjectFilled: BehaviorSubject<boolean>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: any;
  constructor(
    private dispenseCommonOperationsService: DispenseCommonOperationsService,
    private dispenseApi: DispenseApi,
  ) {
    super();
  }

  mapDispenseDataToDispensePointInTimeData(
    dispenseItems: DispenseItem[],
    attendingPhysician: Physician,
    patient: Patient,
    clinic: PracticeClinicDetail,
    medOrders: DispenseSelectedGroup[] = [],
  ): DispenseSnapshotViewModel {
    if (!dispenseItems) {
      throw new Error('dispense items not provided');
    }
    if (!patient) {
      throw new Error('patient info not provided');
    }
    if (!clinic) {
      throw new Error('clinic info not provided');
    }
    const dispenseSnapshot: DispenseSnapshotViewModel = new DispenseSnapshotViewModel();
    this.dispenseCommonOperationsService.mapAttendingPhysician(attendingPhysician, dispenseSnapshot);
    this.mapPatient(patient, dispenseSnapshot);
    this.fillProductInfo(dispenseItems, dispenseSnapshot, medOrders);
    this.dispenseCommonOperationsService.mapClinic(clinic, dispenseSnapshot);
    dispenseSnapshot.stationHost = this.dispenseCommonOperationsService.getStationName();
    dispenseSnapshot.dispensedByUserId = this.dispenseCommonOperationsService.getUserId();
    dispenseSnapshot.dispensedByUserName = this.dispenseCommonOperationsService.getUserName();
    dispenseSnapshot.dispensedByFirstName = this.dispenseCommonOperationsService.getUserFirstName();
    dispenseSnapshot.dispensedByLastName = this.dispenseCommonOperationsService.getUserLastName();
    return dispenseSnapshot;
  }

  getPointInTimePrintData(transactionIds: number[], inclusionIds: number[]): Observable<Array<DispenseSnapshotViewModel>> {
    const searchParam: DispenseSnapshotPrintDataSearchParameter = {
      transactionIds,
      supplierInclusionIds: inclusionIds,
    };
    return this.dispenseApi.getDispenseSnapshotDataForPrinting(searchParam).pipe(
      catchError((error: Error) => {
        this.handleError(error);
        return throwError(error);
      }), map(dispenseSnapshots => {
        const dispenseSnapshotsViewModel: Array<DispenseSnapshotViewModel> = [];
        AbsgArrayHelper.each(dispenseSnapshots, (snapshot) => {
          const dispenseSnapshotViewModel = this.mapToDispenseSnapshotViewModel(snapshot);
          const sortedDetails = dispenseSnapshotViewModel.dispenseDetailSnapshot.sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName));
          dispenseSnapshotViewModel.dispenseDetailSnapshot = sortedDetails;
          dispenseSnapshotsViewModel.push(dispenseSnapshotViewModel);
        });
        return dispenseSnapshotsViewModel;
      }));
  }

  getMedOrderDataForPrinting(transactionDetailIdList: number[]): Observable<Array<MedOrderPrintDetail>> {
    return this.dispenseApi.getMedOrderDataForPrinting(transactionDetailIdList).pipe(
      catchError((error: Error) => {
        this.handleError(error);
        return throwError(error);
      }),
    );
  }

  getPhysicianInfo(dispenseSnanpshot: DispenseSnapshotViewModel): Physician {
    if (!dispenseSnanpshot) {
      throw new Error('dispense snapshot object not provided');
    }
    const physician = new Physician();
    physician.firstName = dispenseSnanpshot.attendingPhysicianFirstName;
    physician.lastName = dispenseSnanpshot.attendingPhysicianLastName;
    return physician;
  }
  getAdditionalPITInfo(dispenseSnanpshot: DispenseSnapshotViewModel): AdditionalPointInTimeData {
    if (!dispenseSnanpshot) {
      throw new Error('dispense snapshot object not provided');
    }
    const additionalPointInTimeData = new AdditionalPointInTimeData();
    additionalPointInTimeData.dispensedByUserId = dispenseSnanpshot.dispensedByUserId;
    additionalPointInTimeData.dispensedByUserName = dispenseSnanpshot.dispensedByUserName;
    additionalPointInTimeData.dispensedByFirstName = dispenseSnanpshot.dispensedByFirstName;
    additionalPointInTimeData.dispensedByLastName = dispenseSnanpshot.dispensedByLastName;
    additionalPointInTimeData.stationHost = dispenseSnanpshot.stationHost;
    return additionalPointInTimeData;
  }
  getPatientInfo(dispenseSnanpshot: DispenseSnapshotViewModel, patientHistoryPatientId: number): Patient {
    if (!dispenseSnanpshot) {
      throw new Error('dispense snapshot object not provided');
    }
    const patient: Patient = new Patient();
    patient.admittingPhysician = new Physician();
    patient.firstName = dispenseSnanpshot.patientFirstName;
    patient.lastName = dispenseSnanpshot.patientLastName;
    patient.dateOfBirth = dispenseSnanpshot.dateOfBirth;
    patient.patientId = dispenseSnanpshot.patientId;
    patient.id = patientHistoryPatientId;
    if (dispenseSnanpshot.allergies) {
      patient.allergies = dispenseSnanpshot.allergies.split(', ');
    }
    patient.admittingPhysician.firstName = dispenseSnanpshot.attendingPhysicianFirstName;
    patient.admittingPhysician.lastName = dispenseSnanpshot.attendingPhysicianLastName;
    return patient;
  }

  getProductInfo(dispenseSnanpshot: DispenseSnapshotViewModel): DispenseItem[] {
    if (!dispenseSnanpshot) {
      throw new Error('dispense snapshot object not provided');
    }
    const dispenseItems: Array<DispenseItem> = [];
    dispenseSnanpshot.dispenseDetailSnapshot.forEach(elem => {
      if (!AbsgArrayHelper.isEmpty(elem.txnDetailIdList)) {
        AbsgArrayHelper.forEach(elem.txnDetailIdList, (txnDetailId: number) => {
          const dItem = this.createDispenseItemForPrinting(elem, dispenseSnanpshot);
          dItem.inventoryTransactionDetailId = txnDetailId;
          dispenseItems.push(dItem);
        });
      } else {
        dispenseItems.push(this.createDispenseItemForPrinting(elem, dispenseSnanpshot));
      }
    });
    return dispenseItems;
  }

  private createDispenseItemForPrinting(elem: DispenseDetailSnapshotViewModel, dispenseSnanpshot: DispenseSnapshotViewModel): DispenseItem {
    const dispenseItem = new DispenseItem();
    dispenseItem.inventoryItem = new InventoryItem();
    dispenseItem.inventoryItem.gcnSequenceNumber = elem.gcnSequenceNumber;
    dispenseItem.inventoryItem.genericName = elem.genericName;
    dispenseItem.inventoryItem.alternateName = elem.alternateName;
    dispenseItem.inventoryItem.bundleLabelCode = elem.bundleLabelCode;
    dispenseItem.inventoryItem.bundleLabelName = elem.bundleLabelName;
    dispenseItem.inventoryItem.dispenseUnit = elem.dispenseUnitOfMeasureDisplay;
    dispenseItem.inventoryItem.dispenseUOMConversionFactor = elem.dispenseUnitOfMeasureConversionFactor;
    dispenseItem.inventoryItem.volumeUOMConversionFactor = elem.volumeUnitOfMeasureConversionFactor;
    dispenseItem.inventoryItem.name = elem.displayName;
    dispenseItem.inventoryItem.volumeUnit = elem.volumeUnitOfMeasureDisplay;
    dispenseItem.inventoryItem.infuseNotes1 = elem.infuseNotes1;
    dispenseItem.inventoryItem.infuseNotes2 = elem.infuseNotes2;
    dispenseItem.inventoryItem.infuseNotes3 = elem.infuseNotes3;
    dispenseItem.inventoryItem.infusionNoteOnReceipt = elem.displayInfusionNotesOnReceipt;
    dispenseItem.inventoryItem.infusionNoteOnLabel = elem.displayInfusionNotesOnLabel;
    dispenseItem.inventoryItem.dispenseTypeId = elem.dispenseTypeId;
    dispenseItem.inventoryItem.dispenseTypeName = elem.dispenseTypeName;
    dispenseItem.inventoryItem.inventoryUOMConversionFactor = elem.inventoryUnitOfMeasureConversionFactor;
    dispenseItem.inventoryItem.purchaseUOMConversionFactor = elem.purchaseUnitOfMeasureConversionFactor;
    dispenseItem.inventoryItem.inventoryUnit = elem.inventoryUnitOfMeasureDisplay;
    dispenseItem.inventoryItem.ndc = elem.unitNdc;
    dispenseItem.inventoryItem.packageNdc = elem.packageNdc;
    dispenseItem.inventoryItem.tradeName = elem.tradeName;
    dispenseItem.inventoryItem.isPracticeFavorite = elem.isPracticeFavorite;
    dispenseItem.inventoryItem.formularyTypeId = elem.formularyTypeId;
    dispenseItem.inventoryItem.isDiscontinued = elem.isDiscontinued;
    dispenseItem.inventoryItem.supplierProductInclusionId = elem.supplierProductInclusionId;
    dispenseItem.inventoryItem.itemId = elem.itemId;
    dispenseItem.inventoryItem.is340B = elem.is340B;
    dispenseItem.inventoryItem.isBuyIn = elem.isBuyIn;
    dispenseItem.inventoryItem.id = elem.inventoryItemId;
    dispenseItem.wasteQuantity = CalculationHelper.add(elem.wasteQtyAfterDispense, elem.wasteQtyAtDispense);
    const qty = CalculationHelper.subtract(elem.wasteQtyAtDispense, elem.returnQtyAfterDispense);
    if (qty > 0) {
      dispenseItem.patientAdministerQuantity = CalculationHelper.subtract(elem.totalDispenseQty,
        CalculationHelper.add(elem.wasteQtyAfterDispense, elem.returnQtyAfterDispense));
    } else {
      dispenseItem.patientAdministerQuantity = CalculationHelper.subtract(
        CalculationHelper.subtract(elem.totalDispenseQty, (qty * -1)), dispenseItem.wasteQuantity);
    }
    dispenseItem.inventoryItem.storagePosition = new StoragePosition();
    dispenseItem.inventoryItem.storagePosition.parentContainerName = elem.accessPointName;
    dispenseItem.inventoryItem.storagePosition.name = `${elem.accessPointName} - ${elem.positionName}`;
    dispenseItem.inventoryItem.autoPrintLabel = elem.autoPrintLabel;
    dispenseItem.inventoryItem.autoPrintReceipt = elem.autoPrintReceipt;
    dispenseItem.inventoryItem.requiredNote = new RequiredNote();
    dispenseItem.inventoryItem.patientId = elem.patientId;
    dispenseItem.inventoryItem.patientOwnedItemTypeId = elem.patientOwnedItemTypeId;
    dispenseItem.fromCabinetQuantity = elem.fromCabinetQty;
    dispenseItem.fromUnderHoodQuantity = elem.fromUnderHoodQty;
    dispenseItem.toCabinetQuantity = elem.toCabinetQty;
    dispenseItem.toUnderHoodQuantity = elem.toUnderHoodQty;
    dispenseItem.transactionId = elem.transactionId;
    dispenseItem.inventoryTransactionDetailId = elem.transactionInventoryDetailID;
    dispenseItem.inventoryItem.witnessRequired = elem.isWitnessRequired;

    dispenseItem.inventoryItem.patientInfo = {
      idNumber: elem.patientOwnedPatientIdNumber,
      firstName: elem.patientOwnedPatientFirstName,
      lastName: elem.patientOwnedPatientLastName,
    } as PatientLite;
    dispenseItem.inventoryItem.patientOwnedItemTypeShortName = elem.patientOwnedItemTypeShortName;

    const productGroupProduct: ProductGroupProduct = {
      productGroupTypeId: elem.productGroupTypeId,
    };
    dispenseItem.inventoryItem.productGroupProducts = [];
    dispenseItem.inventoryItem.productGroupProducts.push(productGroupProduct);
    dispenseItem.ndcLotList = [];
    if (!elem.unitNdc) {
      elem.unitNdc = '';
    }
    dispenseItem.ndcLotList.push(new NdcDetail(elem.unitNdc, []));
    if (elem.lotNumbers) {
      elem.lotNumbers.forEach(lot => {
        dispenseItem.inventoryItem.multiLotYN = true;
        dispenseItem.ndcLotList[0].lotDetails.push(new LotDetail(lot));
      });
    }

    if (dispenseSnanpshot.activities && dispenseSnanpshot.activities.length > 0) {
      //Find activities based on supplierProductInclusionId and transactionInventoryDetailId as
      //as Patient Owned details is not reflecting properly in activities
      const requiredNotes = dispenseSnanpshot.activities && dispenseSnanpshot.activities
        .find(activity => activity.transactionId === dispenseItem.transactionId
          && activity.activityTypeId === InventoryActivityTypes.DispenseRequiredNotes
          && activity.supplierProductInclusionId === elem.supplierProductInclusionId
          && activity.transactionInventoryDetailId === dispenseItem.inventoryTransactionDetailId);

      if (requiredNotes) {
        const requiredNote = new RequiredNote();
        requiredNote.isNotesRequired = true;
        requiredNote.label = requiredNotes.requireNote;
        requiredNote.note = requiredNotes.requireNoteValue;
        requiredNote.printNotesOnLabel = elem.displayRequiredNoteOnLabel;
        requiredNote.printNotesOnReceipt = elem.displayRequiredNoteOnReceipt;
        dispenseItem.inventoryItem.requiredNote = requiredNote;
      } else {
        dispenseItem.inventoryItem.requiredNote = null;
      }

      // filtering label notes, sorting and picking first one as most latest label note
      const filteredLabelNotes = AbsgArrayHelper.filter(dispenseSnanpshot.activities, activity => activity.transactionId === dispenseItem.transactionId
        && activity.activityTypeId === InventoryActivityTypes.DispenseLabelNotes
        && activity.supplierProductInclusionId === elem.supplierProductInclusionId
        && activity.patientOwnedPatientId === elem.patientId
        && activity.patientOwnedItemTypeId === elem.patientOwnedItemTypeId);

      if (filteredLabelNotes && filteredLabelNotes.length > 0) {
        const mostRecentLabelNote = filteredLabelNotes.sort((labelNote1, labelNote2) => labelNote1.activityId > labelNote2.activityId ? -1 : 1)[0];
        dispenseItem.labelLine1 = mostRecentLabelNote.labelNote1;
        dispenseItem.labelLine2 = mostRecentLabelNote.labelNote2;
        dispenseItem.labelLine3 = mostRecentLabelNote.labelNote3;
      } else {
        dispenseItem.labelLine1 = null;
        dispenseItem.labelLine2 = null;
        dispenseItem.labelLine3 = null;
      }
    } else {
      dispenseItem.inventoryItem.requiredNote = null;
      dispenseItem.labelLine1 = null;
      dispenseItem.labelLine2 = null;
      dispenseItem.labelLine3 = null;
    }

    dispenseItem.inventoryItem.instructionOnLabel = elem.displayInstructionsOnLabel;
    dispenseItem.inventoryItem.routeOnLabel = elem.displayRouteOnLabel;
    dispenseItem.inventoryItem.fluidOnLabel = elem.displayFluidOnLabel;
    dispenseItem.inventoryItem.durationOnLabel = elem.displayDurationOnLabel;
    dispenseItem.instructions = [elem.instructions];
    dispenseItem.route = elem.route;

    return dispenseItem;
  }

  getClinicInfo(dispenseSnanpshot: DispenseSnapshotViewModel): PracticeClinicDetail {
    if (!dispenseSnanpshot) {
      throw new Error('dispense snapshot object not provided');
    }
    const clinicDetailStro: PracticeClinicDetail = {
      clinicName: dispenseSnanpshot.clinicName,
      practiceName: dispenseSnanpshot.practiceName,
      phoneNumbers: [],
      addresses: [],
      practiceAddresses: [],
      practicePhoneNumbers: [],
    };
    const phoneNumbeddr: Telephone = {
      contactNumber: dispenseSnanpshot.clinicPhoneNumber,
    };
    clinicDetailStro.phoneNumbers.push(phoneNumbeddr);
    const physicalAddress: PhysicalAddress = {
      address1: dispenseSnanpshot.clinicAddressLine1,
      city: dispenseSnanpshot.clinicCity,
      state: dispenseSnanpshot.clinicState,
      zip: dispenseSnanpshot.clinicZipCode,
    };
    clinicDetailStro.practiceAddresses.push(physicalAddress);
    clinicDetailStro.addresses.push(physicalAddress);
    return clinicDetailStro;
  }

  getMedOrderLabelInfo(medOrders: DispenseSelectedGroup[], dispenseItem: DispenseItem): [string, string] {
    const instructions: string[] = [];
    const routes: string[] = [];
    if (medOrders && medOrders.length > 0) {
      const associatedMedOrders = medOrders.filter((group: DispenseSelectedGroup) => group.dispenseItems.some((di: DispenseItem) =>
        di.inventoryItem.supplierProductInclusionId === dispenseItem.inventoryItem.supplierProductInclusionId));
      if (associatedMedOrders && associatedMedOrders.length > 0) {
        associatedMedOrders.forEach(mo => {
          instructions.push(mo.medOrderDetail.detail.instructions);
          routes.push(mo.medOrderDetail.detail.route);
        });
      }
    }
    // limiting length of instruction and route based on column size in the table dispensedetailsnapshot
    return [instructions.join().substring(0, 1000), routes.join().substring(0, 10)];
  }

  private mapToDispenseSnapshotViewModel(dispenseSnapshot: DispenseSnapshot): DispenseSnapshotViewModel {
    const dispenseSnapshotsViewModel = new DispenseSnapshotViewModel();
    dispenseSnapshotsViewModel.allergies = dispenseSnapshot.allergies;
    dispenseSnapshotsViewModel.attendingPhysicianFirstName = dispenseSnapshot.attendingPhysicianFirstName;
    dispenseSnapshotsViewModel.attendingPhysicianLastName = dispenseSnapshot.attendingPhysicianLastName;
    dispenseSnapshotsViewModel.clinicAddressLine1 = dispenseSnapshot.clinicAddressLine1;
    dispenseSnapshotsViewModel.clinicCity = dispenseSnapshot.clinicCity;
    dispenseSnapshotsViewModel.clinicName = dispenseSnapshot.clinicName;
    dispenseSnapshotsViewModel.clinicPhoneNumber = dispenseSnapshot.clinicPhoneNumber;
    dispenseSnapshotsViewModel.clinicState = dispenseSnapshot.clinicState;
    dispenseSnapshotsViewModel.clinicZipCode = dispenseSnapshot.clinicZipCode;
    dispenseSnapshotsViewModel.dateOfBirth = dispenseSnapshot.dateOfBirth;
    dispenseSnapshotsViewModel.dispenseSnapshotId = dispenseSnapshot.dispenseSnapshotId;
    dispenseSnapshotsViewModel.dispensedByUserName = dispenseSnapshot.dispensedByUserName;
    dispenseSnapshotsViewModel.dispensedByFirstName = dispenseSnapshot.dispensedByFirstName;
    dispenseSnapshotsViewModel.dispensedByLastName = dispenseSnapshot.dispensedByLastName;
    dispenseSnapshotsViewModel.patientFirstName = dispenseSnapshot.patientFirstName;
    dispenseSnapshotsViewModel.patientId = dispenseSnapshot.patientIdNumber;
    dispenseSnapshotsViewModel.patientLastName = dispenseSnapshot.patientLastName;
    dispenseSnapshotsViewModel.practiceName = dispenseSnapshot.practiceName;
    dispenseSnapshotsViewModel.stationHost = dispenseSnapshot.stationHost;
    dispenseSnapshotsViewModel.transactionId = dispenseSnapshot.transactionId;
    dispenseSnapshotsViewModel.serviceDate = this.convertUtcToLocal(dispenseSnapshot.serviceDate);
    dispenseSnapshotsViewModel.activities = dispenseSnapshot.activities;
    dispenseSnapshotsViewModel.dispenseDetailSnapshot
      = this.mapToDispenseDetailsSnapshotViewModel(dispenseSnapshot.dispenseDetailSnapshot);
    return dispenseSnapshotsViewModel;
  }

  private mapToDispenseDetailsSnapshotViewModel(dispenseDetailsSnapshot: Array<DispenseDetailSnapshot>): Array<DispenseDetailSnapshotViewModel> {
    const dispenseDetailsSnapshotViewModel: Array<DispenseDetailSnapshotViewModel> = [];
    AbsgArrayHelper.each(dispenseDetailsSnapshot, (dispenseDetailSnapshot) => {
      const dispenseDetailSnapshotViewModel: DispenseDetailSnapshotViewModel = new DispenseDetailSnapshotViewModel();
      dispenseDetailSnapshotViewModel.transactionId = dispenseDetailSnapshot.transactionId;
      dispenseDetailSnapshotViewModel.supplierProductInclusionId = dispenseDetailSnapshot.supplierProductInclusionId;
      dispenseDetailSnapshotViewModel.transactionInventoryDetailID = dispenseDetailSnapshot.transactionInventoryDetailID;
      dispenseDetailSnapshotViewModel.gcnSequenceNumber = dispenseDetailSnapshot.gcnSequenceNumber;
      dispenseDetailSnapshotViewModel.genericName = dispenseDetailSnapshot.genericName;
      dispenseDetailSnapshotViewModel.displayName = dispenseDetailSnapshot.displayName;
      dispenseDetailSnapshotViewModel.dispenseDetailSnapshotId = dispenseDetailSnapshot.dispenseDetailSnapshotId;
      dispenseDetailSnapshotViewModel.dispenseDetailSnapshotId = dispenseDetailSnapshot.dispenseDetailSnapshotId;
      dispenseDetailSnapshotViewModel.inventoryUnitOfMeasureConversionFactor
        = dispenseDetailSnapshot.inventoryUnitOfMeasureConversionFactor;
      dispenseDetailSnapshotViewModel.inventoryUnitOfMeasureDisplay = dispenseDetailSnapshot.inventoryUnitOfMeasureDisplay;
      dispenseDetailSnapshotViewModel.volumeUnitOfMeasureConversionFactor
        = dispenseDetailSnapshot.volumeUnitOfMeasureConversionFactor;
      dispenseDetailSnapshotViewModel.volumeUnitOfMeasureDisplay = dispenseDetailSnapshot.volumeUnitOfMeasureDisplay;
      dispenseDetailSnapshotViewModel.purchaseUnitOfMeasureConversionFactor
        = dispenseDetailSnapshot.purchaseUnitOfMeasureConversionFactor;
      dispenseDetailSnapshotViewModel.infuseNotes1 = dispenseDetailSnapshot.infuseNotes1;
      dispenseDetailSnapshotViewModel.infuseNotes2 = dispenseDetailSnapshot.infuseNotes2;
      dispenseDetailSnapshotViewModel.infuseNotes3 = dispenseDetailSnapshot.infuseNotes3;
      dispenseDetailSnapshotViewModel.dispenseDetailSnapshotId = dispenseDetailSnapshot.dispenseDetailSnapshotId;
      dispenseDetailSnapshotViewModel.unitNdc = dispenseDetailSnapshot.unitNdc;
      dispenseDetailSnapshotViewModel.packageNdc = dispenseDetailSnapshot.packageNdc;
      dispenseDetailSnapshotViewModel.tradeName = dispenseDetailSnapshot.tradeName;
      dispenseDetailSnapshotViewModel.isPracticeFavorite = dispenseDetailSnapshot.isPracticeFavorite;
      dispenseDetailSnapshotViewModel.isDiscontinued = dispenseDetailSnapshot.isDiscontinued;
      dispenseDetailSnapshotViewModel.formularyTypeId = dispenseDetailSnapshot.formularyTypeId;
      dispenseDetailSnapshotViewModel.alternateName = dispenseDetailSnapshot.alternateName;
      dispenseDetailSnapshotViewModel.purchaseUnitOfMeasureDisplay = dispenseDetailSnapshot.purchaseUnitOfMeasureDisplay;
      dispenseDetailSnapshotViewModel.dispenseDetailSnapshotId = dispenseDetailSnapshot.dispenseDetailSnapshotId;
      dispenseDetailSnapshotViewModel.itemId = dispenseDetailSnapshot.itemId;
      dispenseDetailSnapshotViewModel.isRefrigerated = dispenseDetailSnapshot.isRefrigerated;
      dispenseDetailSnapshotViewModel.lotNumbers = dispenseDetailSnapshot.lotNumbers;
      dispenseDetailSnapshotViewModel.expirationDate = dispenseDetailSnapshot.expirationDate;
      dispenseDetailSnapshotViewModel.bundleLabelCode = dispenseDetailSnapshot.bundleLabelCode;
      dispenseDetailSnapshotViewModel.bundleLabelName = dispenseDetailSnapshot.bundleLabelName;
      dispenseDetailSnapshotViewModel.positionName = dispenseDetailSnapshot.positionName;
      dispenseDetailSnapshotViewModel.accessPointName = dispenseDetailSnapshot.accessPointName;
      dispenseDetailSnapshotViewModel.autoPrintLabel = dispenseDetailSnapshot.autoPrintLabel;
      dispenseDetailSnapshotViewModel.autoPrintLabel = dispenseDetailSnapshot.autoPrintLabel;
      dispenseDetailSnapshotViewModel.lotNumbers = dispenseDetailSnapshot.lotNumbers;
      dispenseDetailSnapshotViewModel.wasteQtyAfterDispense = dispenseDetailSnapshot.wasteQtyAfterDispense;
      dispenseDetailSnapshotViewModel.wasteQtyAtDispense = dispenseDetailSnapshot.wasteQtyAtDispense;
      dispenseDetailSnapshotViewModel.returnQtyAfterDispense = dispenseDetailSnapshot.returnQtyAfterDispense;
      dispenseDetailSnapshotViewModel.returnQtyAtDispense = dispenseDetailSnapshot.returnQtyAtDispense;
      dispenseDetailSnapshotViewModel.fromCabinetQty = dispenseDetailSnapshot.fromCabinetQty;
      dispenseDetailSnapshotViewModel.fromUnderHoodQty = dispenseDetailSnapshot.fromUnderHoodQty;
      dispenseDetailSnapshotViewModel.outboundQty = dispenseDetailSnapshot.outboundQty;
      dispenseDetailSnapshotViewModel.toCabinetQty = dispenseDetailSnapshot.toCabinetQty;
      dispenseDetailSnapshotViewModel.totalDispenseQty = dispenseDetailSnapshot.totalDispenseQty;
      dispenseDetailSnapshotViewModel.toUnderHoodQty = dispenseDetailSnapshot.toUnderHoodQty;
      dispenseDetailSnapshotViewModel.displayInfusionNotesOnLabel = dispenseDetailSnapshot.displayInfusionNotesOnLabel;
      dispenseDetailSnapshotViewModel.displayRequiredNoteOnLabel = dispenseDetailSnapshot.displayRequiredNoteOnLabel;
      dispenseDetailSnapshotViewModel.displayInfusionNotesOnReceipt = dispenseDetailSnapshot.displayInfusionNotesOnReceipt;
      dispenseDetailSnapshotViewModel.displayRequiredNoteOnReceipt = dispenseDetailSnapshot.displayRequiredNoteOnReceipt;
      dispenseDetailSnapshotViewModel.dispenseUnitOfMeasureDisplay = dispenseDetailSnapshot.dispenseUnitOfMeasureDisplay;
      dispenseDetailSnapshotViewModel.dispenseUnitOfMeasureConversionFactor
        = dispenseDetailSnapshot.dispenseUnitOfMeasureConversionFactor;
      dispenseDetailSnapshotViewModel.productGroupTypeId = dispenseDetailSnapshot.productGroupTypeId;
      dispenseDetailSnapshotViewModel.dispenseTypeId = dispenseDetailSnapshot.dispenseTypeId;
      dispenseDetailSnapshotViewModel.dispenseTypeName = dispenseDetailSnapshot.dispenseTypeName;
      dispenseDetailSnapshotViewModel.isWitnessRequired = dispenseDetailSnapshot.isWitnessRequired;
      dispenseDetailSnapshotViewModel.instructions = dispenseDetailSnapshot.instructions;
      dispenseDetailSnapshotViewModel.displayInstructionsOnLabel = dispenseDetailSnapshot.displayInstructionsOnLabel;
      dispenseDetailSnapshotViewModel.route = dispenseDetailSnapshot.route;
      dispenseDetailSnapshotViewModel.displayRouteOnLabel = dispenseDetailSnapshot.displayRouteOnLabel;
      dispenseDetailSnapshotViewModel.displayFluidOnLabel = dispenseDetailSnapshot.displayFluidOnLabel;
      dispenseDetailSnapshotViewModel.displayDurationOnLabel = dispenseDetailSnapshot.displayDurationOnLabel;
      dispenseDetailSnapshotViewModel.patientId = dispenseDetailSnapshot.patientId;
      dispenseDetailSnapshotViewModel.patientOwnedItemTypeId = dispenseDetailSnapshot.patientOwnedItemTypeId;
      dispenseDetailSnapshotViewModel.inventoryItemId = dispenseDetailSnapshot.inventoryItemId;
      dispenseDetailSnapshotViewModel.patientOwnedPatientIdNumber = dispenseDetailSnapshot.patientOwnedPatientIdNumber;
      dispenseDetailSnapshotViewModel.patientOwnedPatientFirstName = dispenseDetailSnapshot.patientOwnedPatientFirstName;
      dispenseDetailSnapshotViewModel.patientOwnedPatientLastName = dispenseDetailSnapshot.patientOwnedPatientLastName;
      dispenseDetailSnapshotViewModel.patientOwnedItemTypeShortName = dispenseDetailSnapshot.patientOwnedItemTypeShortName;
      dispenseDetailSnapshotViewModel.txnDetailIdList = dispenseDetailSnapshot.txnDetailIdList;
      dispenseDetailsSnapshotViewModel.push(dispenseDetailSnapshotViewModel);
    });
    return dispenseDetailsSnapshotViewModel;
  }

  private mapPatient(patient: Patient, dispenseSnanpshot: DispenseSnapshotViewModel): void {
    this.dispenseCommonOperationsService.mapPatient(patient, dispenseSnanpshot);
    if (patient && patient.allergies) {
      dispenseSnanpshot.allergies = patient.allergies.join(', ');
    }
  }

  private fillProductInfo(dispenseItems: DispenseItem[], dispenseSnanpshot: DispenseSnapshotViewModel,
    medOrders: DispenseSelectedGroup[]): void {
    dispenseSnanpshot.dispenseDetailSnapshot = new Array<DispenseDetailSnapshotViewModel>();
    if (dispenseItems && dispenseItems.length > 0) {
      dispenseItems = AbsgArrayHelper.uniqBy(dispenseItems, e =>
        [e.inventoryItem.supplierProductInclusionId, e.inventoryItem.patientId, e.inventoryItem.patientOwnedItemTypeId].join());
      dispenseItems.forEach(dispenseItem => {
        const medOrderLabelInfo = this.getMedOrderLabelInfo(medOrders, dispenseItem);
        let inventoryItem = dispenseItem.inventoryItem;
        if (inventoryItem) {

          if (inventoryItem.isBinned()) {
            inventoryItem = this.dispenseCommonOperationsService.mapMEGGroupReceiptAndLabel(inventoryItem);
          }

          const dispenseDetailSnapshot: DispenseDetailSnapshotViewModel = {
            gcnSequenceNumber: inventoryItem.gcnSequenceNumber,
            genericName: inventoryItem.genericName,
            dispenseUnitOfMeasureDisplay: inventoryItem.dispenseUnit,
            dispenseUnitOfMeasureConversionFactor: inventoryItem.dispenseUOMConversionFactor,
            volumeUnitOfMeasureConversionFactor: inventoryItem.volumeUOMConversionFactor,
            displayName: inventoryItem.name,
            volumeUnitOfMeasureDisplay: inventoryItem.volumeUnit,
            infuseNotes1: inventoryItem.infuseNotes1,
            infuseNotes2: inventoryItem.infuseNotes2,
            infuseNotes3: inventoryItem.infuseNotes3,
            dispenseTypeId: inventoryItem.dispenseTypeId,
            dispenseTypeName: inventoryItem.dispenseTypeName,
            inventoryUnitOfMeasureConversionFactor: inventoryItem.inventoryUOMConversionFactor,
            purchaseUnitOfMeasureConversionFactor: inventoryItem.purchaseUOMConversionFactor,
            inventoryUnitOfMeasureDisplay: inventoryItem.inventoryUnit,
            unitNdc: inventoryItem.ndc,
            packageNdc: inventoryItem.packageNdc,
            tradeName: inventoryItem.tradeName,
            isPracticeFavorite: inventoryItem.isPracticeFavorite,
            formularyTypeId: inventoryItem.formularyTypeId,
            isDiscontinued: inventoryItem.isDiscontinued,
            dispenseDetailSnapshotId: 0,
            supplierProductInclusionId: inventoryItem.supplierProductInclusionId,
            itemId: inventoryItem.itemId,
            autoPrintLabel: inventoryItem.autoPrintLabel,
            autoPrintReceipt: inventoryItem.autoPrintReceipt,
            alternateName: inventoryItem.alternateName,
            isRefrigerated: inventoryItem.refrigerated,
            bundleLabelCode: inventoryItem.bundleLabelCode,
            bundleLabelName: inventoryItem.bundleLabelName,
            displayInfusionNotesOnLabel: inventoryItem.infusionNoteOnLabel,
            displayInfusionNotesOnReceipt: inventoryItem.infusionNoteOnReceipt,
            displayRequiredNoteOnLabel: inventoryItem.requiredNoteOnLabel,
            displayRequiredNoteOnReceipt: inventoryItem.requiredNoteOnReceipt,
            isWitnessRequired: inventoryItem.witnessRequired,
            displayInstructionsOnLabel: inventoryItem.instructionOnLabel,
            displayRouteOnLabel: inventoryItem.routeOnLabel,
            displayFluidOnLabel: inventoryItem.fluidOnLabel,
            displayDurationOnLabel: inventoryItem.durationOnLabel,
            instructions: medOrderLabelInfo[0],
            route: medOrderLabelInfo[1],
            is340B: inventoryItem.is340B,
            isBuyIn: inventoryItem.isBuyIn,
            patientId: inventoryItem.patientId,
            patientOwnedItemTypeId: inventoryItem.patientOwnedItemTypeId,
            patientOwnedPatientIdNumber: inventoryItem.patientInfo?.idNumber,
            patientOwnedPatientFirstName: inventoryItem.patientInfo?.firstName,
            patientOwnedPatientLastName: inventoryItem.patientInfo?.lastName,
            patientOwnedPatientDOB: inventoryItem.patientInfo?.birthDate,
            patientOwnedItemTypeLongName: inventoryItem.patientOwnedItemTypeFullName,
            patientOwnedItemTypeShortName: inventoryItem.patientOwnedItemTypeShortName,
            isItem505b2: inventoryItem.isItem505b2,
          };
          if (inventoryItem.storagePosition) {
            dispenseDetailSnapshot.accessPointName = inventoryItem.storagePosition.parentContainerName;
            dispenseDetailSnapshot.positionName = inventoryItem.getPositionName();
          }
          if (inventoryItem.productGroupProducts && inventoryItem.productGroupProducts.length > 0) {
            // take first because from UI we are only allowed to insert one.
            dispenseDetailSnapshot.productGroupTypeId = inventoryItem.productGroupProducts[0].productGroupTypeId;
          }

          const requiredNotes = dispenseSnanpshot.activities && dispenseSnanpshot.activities
            .find(activity => activity.transactionId === dispenseItem.transactionId
              && activity.activityType.activityTypeId === InventoryActivityTypes.DispenseRequiredNotes
              && activity.supplierProductInclusionId === inventoryItem.supplierProductInclusionId
              && activity.patientOwnedPatientId === inventoryItem.patientId
              && activity.patientOwnedItemTypeId === inventoryItem.patientOwnedItemTypeId);

          if (requiredNotes) {
            const requiredNote = new RequiredNote();
            requiredNote.label = requiredNotes.requireNote;
            requiredNote.note = requiredNotes.requireNoteValue;
            dispenseDetailSnapshot.requiredNotes = requiredNote;
          } else {
            dispenseDetailSnapshot.requiredNotes = null;
          }

          dispenseSnanpshot.dispenseDetailSnapshot.push(dispenseDetailSnapshot);
        }
      });
    }
  }

  private convertUtcToLocal(utcDate: Date): Date {
    if (!utcDate) {
      return utcDate;
    }

    return DateTimeHelper.convertUTCToLocalJSDate(utcDate);
  }
}
